import React, { useEffect, useState, useRef, useCallback, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ADMIN_DATA_CHANGED_EVENT, ADMIN_PATH, AdminRoute, AdminProvider, AdminRefreshOnAuth } from 'src/admin/core';
import { WEB_APP_DATA_CHANGED_EVENT } from 'src/components/web/appDataEvents';
import WebLoading from 'src/components/web/WebLoading';
import { AddEditFormActionsProvider } from 'src/components/web/AddEditFormActionsContext';
import { useAppConfig } from 'src/components/web/AppConfigContext';
import { Header } from 'src/components/web/Header';
import { isMaintenanceMode, getMaintenanceMessage } from 'src/services/appConfigService';
import IContact from '@constants/interfaces/contacts';
import { IRouteElement, appLinks, DETAILS_ROUTE_PATTERN, EDIT_ROUTE_PATTERN } from 'src/components/web/constants';
import { getUserEmail, getSubscriptionStatusApi } from 'src/services/api';
import { useDataChangedReload } from 'src/hooks/useDataChangedReload';
import ProtectedRoute from 'src/components/web/ProtectedRoute';
import { useNavigate } from 'react-router-dom';

const WebMainPage = lazy(() => import('src/components/web/pages').then((m) => ({ default: m.WebMainPage })));
const WebLoginPage = lazy(() => import('src/components/web/pages').then((m) => ({ default: m.WebLoginPage })));
const WebAddContact = lazy(() => import('src/components/web/pages').then((m) => ({ default: m.WebAddContact })));
const WebEditContact = lazy(() => import('src/components/web/pages').then((m) => ({ default: m.WebEditContact })));
const WebSignUp = lazy(() => import('src/components/web/pages').then((m) => ({ default: m.WebSignUp })));
const WebDetails = lazy(() => import('src/components/web/pages').then((m) => ({ default: m.WebDetails })));
const WebForgotPassword = lazy(() => import('src/components/web/pages').then((m) => ({ default: m.WebForgotPassword })));
const WebResetPassword = lazy(() => import('src/components/web/pages').then((m) => ({ default: m.WebResetPassword })));
const WebChangePassword = lazy(() => import('src/components/web/pages').then((m) => ({ default: m.WebChangePassword })));
const WebAccountPage = lazy(() => import('src/components/web/pages').then((m) => ({ default: m.WebAccountPage })));
const WebPricing = lazy(() => import('src/components/web/pages').then((m) => ({ default: m.WebPricing })));
const WebSubscriptionSuccess = lazy(() => import('src/components/web/pages').then((m) => ({ default: m.WebSubscriptionSuccess })));
const WebRegisterSuccess = lazy(() => import('src/components/web/pages').then((m) => ({ default: m.WebRegisterSuccess })));
const WebTermsPage = lazy(() => import('src/components/web/pages').then((m) => ({ default: m.WebTermsPage })));
const WebPrivacyPage = lazy(() => import('src/components/web/pages').then((m) => ({ default: m.WebPrivacyPage })));
const WebHelpPage = lazy(() => import('src/components/web/pages').then((m) => ({ default: m.WebHelpPage })));

const LazyAdminLayout = lazy(() => import('src/admin').then((m) => ({ default: m.AdminLayout })));
const LazyAdminDashboard = lazy(() => import('src/admin').then((m) => ({ default: m.AdminDashboard })));
const LazyAdminUsers = lazy(() => import('src/admin').then((m) => ({ default: m.AdminUsers })));
const LazyAdminSettings = lazy(() => import('src/admin').then((m) => ({ default: m.AdminSettings })));
const LazyAdminNotifications = lazy(() => import('src/admin').then((m) => ({ default: m.AdminNotifications })));
const LazyAdminAdmins = lazy(() => import('src/admin').then((m) => ({ default: m.AdminAdmins })));

const RouteFallback = () => (
  <div className="flex flex-1 justify-center items-center min-h-[200px]">
    <div className="border-2 border-sky-500 border-t-transparent rounded-full w-8 h-8 animate-spin" />
  </div>
);

const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = useAppConfig();
  const [currentUser, setCurrentUser] = useState<string>('');
  const [contactSelected, setContactSelected] = useState<IContact>();
  const [hasSubscriptionAccess, setHasSubscriptionAccess] = useState<boolean | null>(null);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const prevPathnameRef = useRef<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() => localStorage.getItem('flower_power_notes_user') !== null);
  const [sessionChecked, setSessionChecked] = useState<boolean>(() => !localStorage.getItem('flower_power_notes_user'));

  const maintenanceMode = isMaintenanceMode(config);
  const maintenanceMessage = getMaintenanceMessage(config) || "We'll be back shortly.";

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('fromExtension') !== '1') return;
    const authToken = params.get('authToken');
    const userIdParam = params.get('userId');
    if (!authToken || !userIdParam) return;
    const userId = Number(userIdParam);
    if (!Number.isInteger(userId) || userId < 1) return;
    const user = { AuthToken: authToken, UserId: userId };
    localStorage.setItem('flower_power_notes_user', JSON.stringify(user));
    const cleanUrl = window.location.pathname + (window.location.hash || '');
    window.location.replace(cleanUrl);
  }, []);

  useEffect(() => {
    const pathname = location.pathname;
    if (prevPathnameRef.current !== null && prevPathnameRef.current !== pathname) {
      prevPathnameRef.current = pathname;
      setIsPageLoading(true);
      const t = setTimeout(() => setIsPageLoading(false), 400);
      return () => clearTimeout(t);
    }
    prevPathnameRef.current = pathname;
  }, [location.pathname]);

  const refreshSession = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem('flower_power_notes_user') || '{}');
    const loggedInUserId = user?.UserId;
    const authToken = user?.AuthToken;
    if (!authToken) {
      setSessionChecked(true);
      setHasSubscriptionAccess(null);
      return;
    }
    try {
      const sub = await getSubscriptionStatusApi(authToken);
      if ((sub as { code?: string })?.code === 'ACCOUNT_BLOCKED') {
        localStorage.removeItem('flower_power_notes_user');
        setIsLoggedIn(false);
        setSessionChecked(true);
        navigate(appLinks.LOGIN, { replace: true });
        return;
      }
      setHasSubscriptionAccess(!!sub?.hasAccess);
      if (loggedInUserId) {
        const userEmail = await getUserEmail(loggedInUserId);
        setCurrentUser(userEmail);
      }
    } catch {
      setHasSubscriptionAccess(false);
      if (loggedInUserId) {
        const userEmail = await getUserEmail(loggedInUserId);
        setCurrentUser(userEmail);
      }
    } finally {
      setSessionChecked(true);
    }
  }, [navigate]);

  useEffect(() => {
    refreshSession();
  }, [isLoggedIn, location.pathname, refreshSession]);

  useEffect(() => {
    if (!isLoggedIn) return;
    const handler = () => refreshSession();
    window.addEventListener(ADMIN_DATA_CHANGED_EVENT, handler);
    window.addEventListener(WEB_APP_DATA_CHANGED_EVENT, handler);
    return () => {
      window.removeEventListener(ADMIN_DATA_CHANGED_EVENT, handler);
      window.removeEventListener(WEB_APP_DATA_CHANGED_EVENT, handler);
    };
  }, [isLoggedIn, refreshSession]);

  useDataChangedReload(isLoggedIn);

  useEffect(() => {
    const state = location.state as { fromSubscriptionSuccess?: boolean; fromPricing?: boolean } | undefined;
    const fromSubscriptionSuccess = state?.fromSubscriptionSuccess;
    const fromPricing = state?.fromPricing;
    if (
      isLoggedIn &&
      hasSubscriptionAccess === false &&
      window.location.pathname === appLinks.MAIN_PAGE &&
      !fromSubscriptionSuccess &&
      !fromPricing
    ) {
      navigate(appLinks.PRICING);
    }
  }, [isLoggedIn, hasSubscriptionAccess, navigate, location.state]);

  const routeElements = [
    {
      component: (
        <ProtectedRoute isLoggedIn={isLoggedIn}>
          {hasSubscriptionAccess === false ? (
            <div className="flex flex-1 justify-center items-center p-8">
              <div className="text-center">
                <p className="mb-4 text-gray-700">Subscription required.</p>
                <a href={appLinks.PRICING} className="font-semibold text-sky-600 hover:underline">
                  Go to pricing
                </a>
              </div>
            </div>
          ) : (
            <WebMainPage setContactSelected={setContactSelected} />
          )}
        </ProtectedRoute>
      ),
      link: appLinks.MAIN_PAGE,
    },
    { component: <WebSignUp setCurrentUser={setCurrentUser} setIsLoggedIn={setIsLoggedIn} />, link: appLinks.SIGN_UP },
    {
      component: (
        <WebLoginPage
          setCurrentUser={setCurrentUser}
          setIsLoggedIn={setIsLoggedIn}
          onLoginSuccess={() => setIsPageLoading(true)}
        />
      ),
      link: appLinks.LOGIN,
    },
    { component: <WebForgotPassword />, link: appLinks.FORGOT_PASSWORD },
    { component: <WebResetPassword />, link: appLinks.RESET_PASSWORD },
    {
      component: (
        <ProtectedRoute isLoggedIn={isLoggedIn}>
          <WebChangePassword />
        </ProtectedRoute>
      ),
      link: appLinks.CHANGE_PASSWORD,
    },
    {
      component: (
        <ProtectedRoute isLoggedIn={isLoggedIn}>
          <WebAccountPage setIsLoggedIn={setIsLoggedIn} setCurrentUser={setCurrentUser} />
        </ProtectedRoute>
      ),
      link: appLinks.ACCOUNT,
    },
    { component: <WebPricing />, link: appLinks.PRICING },
    { component: <WebSubscriptionSuccess />, link: appLinks.SUBSCRIPTION_SUCCESS },
    { component: <WebRegisterSuccess />, link: appLinks.REGISTER_SUCCESS },
    { component: <WebTermsPage />, link: appLinks.TERMS },
    { component: <WebPrivacyPage />, link: appLinks.PRIVACY },
    { component: <WebHelpPage />, link: appLinks.HELP },
    {
      component: (
        <ProtectedRoute isLoggedIn={isLoggedIn}>
          <WebAddContact />
        </ProtectedRoute>
      ),
      link: appLinks.ADD,
    },
    {
      component: (
        <ProtectedRoute isLoggedIn={isLoggedIn}>
          <WebEditContact contactSelected={contactSelected} setContactSelected={setContactSelected} />
        </ProtectedRoute>
      ),
      link: EDIT_ROUTE_PATTERN,
    },
    {
      component: (
        <ProtectedRoute isLoggedIn={isLoggedIn}>
          <WebDetails contactSelected={contactSelected} setContactSelected={setContactSelected} />
        </ProtectedRoute>
      ),
      link: DETAILS_ROUTE_PATTERN,
    },
    // Legacy id-less routes: nothing to show, go home.
    { component: <Navigate to={appLinks.MAIN_PAGE} replace />, link: appLinks.EDIT },
    { component: <Navigate to={appLinks.MAIN_PAGE} replace />, link: appLinks.DETAILS },
  ];

  return (
    <AddEditFormActionsProvider>
    <AdminProvider>
    <AdminRefreshOnAuth isLoggedIn={isLoggedIn}>
    <main className="flex flex-col w-full h-screen min-h-0 overflow-hidden">
      {maintenanceMode ? (
        <div className="top-0 right-0 left-0 z-[9999] fixed bg-red-100/90 shadow-md backdrop-blur-md px-4 py-1 w-full font-medium text-red-900 text-sm text-center pointer-events-none">
          {maintenanceMessage}
        </div>
      ) : null}
      <WebLoading isLoading={isPageLoading} />
      {isLoggedIn && !sessionChecked ? (
        <div className="flex flex-col flex-1 justify-center items-center w-full min-h-0">
          <div className="border-2 border-blue-500 border-t-transparent rounded-full w-8 h-8 animate-spin" />
          <p className="mt-3 text-gray-600 text-sm">Checking session...</p>
        </div>
      ) : (
        <>
      {isLoggedIn && currentUser && location.pathname !== appLinks.SUBSCRIPTION_SUCCESS && location.pathname !== appLinks.PRICING && (
        <Header
          currentUser={currentUser}
          setCurrentUser={setCurrentUser}
          setIsLoggedIn={setIsLoggedIn}
          contactSelected={contactSelected}
        />
      )}
              <div
                className={`flex-1 flex flex-col min-h-0 w-full overflow-auto ${
                  isLoggedIn && currentUser && location.pathname !== appLinks.SUBSCRIPTION_SUCCESS && location.pathname !== appLinks.PRICING
                    ? 'pt-20'
                    : ''
                }`}
              >
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {routeElements.map((routeElement: IRouteElement, index: number) => (
              <Route
                path={routeElement.link}
                element={routeElement.component}
                key={index}
              />
            ))}
            <Route
              path={ADMIN_PATH}
              element={
                <AdminRoute isLoggedIn={isLoggedIn}>
                  <div className="flex flex-col flex-1 w-full min-h-0 overflow-hidden">
                    <LazyAdminLayout />
                  </div>
                </AdminRoute>
              }
            >
              <Route index element={<LazyAdminDashboard />} />
              <Route path="users" element={<LazyAdminUsers />} />
              <Route path="settings" element={<LazyAdminSettings />} />
              <Route path="notifications" element={<LazyAdminNotifications />} />
              <Route path="admins" element={<LazyAdminAdmins />} />
            </Route>
          </Routes>
        </Suspense>
      </div>
        </>
      )}
    </main>
    </AdminRefreshOnAuth>
    </AdminProvider>
    </AddEditFormActionsProvider>
  );
};

const App = () => <AppContent />;

export default App;
